import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Building2, User, ShieldCheck, Handshake, Wine, Utensils, Loader2 } from "lucide-react";

const LOGO_URL = "/amazonico-logo.png";
const HERO_B2B = "https://images.pexels.com/photos/34736555/pexels-photo-34736555.jpeg";
const HERO_B2C = "https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg";

const COUNTRIES = [
  { v: "IT", l: "Italia" }, { v: "FR", l: "Francia" }, { v: "ES", l: "Spagna" },
  { v: "PT", l: "Portogallo" }, { v: "DE", l: "Germania" }, { v: "AT", l: "Austria" },
  { v: "CH", l: "Svizzera" }, { v: "UK", l: "Regno Unito" }, { v: "BR", l: "Brasile" },
  { v: "US", l: "Stati Uniti" }, { v: "OTHER", l: "Altro" },
];

const B2B_TYPES = [
  { v: "producer", l: "Produttore / Cantina" },
  { v: "importer", l: "Importatore" },
  { v: "distributor", l: "Distributore" },
  { v: "horeca", l: "Ho.Re.Ca. (ristorazione)" },
  { v: "retail", l: "Retail / GDO" },
  { v: "wine_bar", l: "Wine bar / Enoteca" },
  { v: "other", l: "Altro" },
];

const B2B_CATEGORIES = [
  { v: "wine", l: "Vino & Bollicine", icon: Wine },
  { v: "food", l: "Food & Delicatessen", icon: Utensils },
  { v: "mixed", l: "Wine + Food", icon: Handshake },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();

  const [role, setRole] = useState("buyer");  // default B2B come richiesto
  const [loading, setLoading] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "",
    company: "", vat_number: "", country: "IT",
    contact_phone: "", business_type: "importer", category_focus: "wine",
    website: "", notes: "",
  });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!privacyOk) { toast.error("Devi accettare la privacy policy per continuare"); return; }
    if (form.password.length < 6) { toast.error("Password minima 6 caratteri"); return; }

    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        role,
        country: form.country,
        ...(role === "buyer" && {
          company: form.company.trim(),
          vat_number: form.vat_number.trim(),
          contact_phone: form.contact_phone,
          business_type: form.business_type,
          category_focus: form.category_focus,
          website: form.website,
          notes: form.notes,
        }),
      };
      const u = await register(payload);
      toast.success(u.role === "buyer"
        ? "Registrazione azienda ricevuta! Il tuo account è in attesa di verifica manuale — ti contattiamo entro 24-48h."
        : "Registrazione completata! Benvenuto in Amazonico 3000.");
      if (u.role === "buyer") nav("/b2b"); else nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la registrazione. Riprova.");
    } finally { setLoading(false); }
  };

  const isB2B = role === "buyer";

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#faf7f0] text-stone-900 grid md:grid-cols-[0.9fr_1.1fr]">
      {/* LEFT — visual */}
      <div className="hidden md:block relative overflow-hidden bg-stone-900">
        <img src={isB2B ? HERO_B2B : HERO_B2C} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-black/70" />
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, rgba(201,169,97,0.4) 0%, transparent 55%)" }} />

        <div className="relative h-full flex flex-col justify-between p-10 lg:p-14 text-white">
          <div>
            <Link to="/" className="inline-block">
              <img src={LOGO_URL} alt="Amazonico 3000" className="h-16 md:h-20 object-contain" />
            </Link>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A961] font-bold mb-4">
              {isB2B ? "Diventa Partner · B2B Affiliate" : "Amazonico 3000 · B2C"}
            </div>
            <h1 className="editorial-heading text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              {isB2B ? (
                <>Registra la tua<br /><span className="italic text-[#C9A961]">azienda.</span></>
              ) : (
                <>Un mondo di<br /><span className="italic text-[#C9A961]">eccellenze</span> italiane.</>
              )}
            </h1>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              {isB2B
                ? "Vetrina B2B, AI matching predittivo, servizi export, presenza in Brasile e rivista mensile. Un unico ecosistema per portare il tuo brand nel mondo."
                : "Food box selezionati, vini pregiati, ricette AI, esperienze immersive. Il meglio del Made in Italy a casa tua."}
            </p>

            {isB2B ? (
              <ul className="mt-8 space-y-3">
                {[
                  "Vetrina digitale 24/7 verso buyer in 10+ paesi",
                  "Compliance MAPA · ANVISA · SISCOMEX inclusa",
                  "Brazil Desk operativo a San Paolo",
                  "Rivista mensile & catalogo annuale 2026-2027",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                    <ShieldCheck className="h-4 w-4 text-[#C9A961] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className="mt-8 space-y-3">
                {[
                  "Food box mensili con degustazione narrata",
                  "AI Sommelier · abbinamenti in tempo reale",
                  "Esperienze in vigna e tour delle cantine",
                  "Spedizione premium in tutta Italia",
                ].map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                    <ShieldCheck className="h-4 w-4 text-[#C9A961] mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">
            Amazonico 3000 · ExportTech · Italia → Mondo
          </div>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex items-start md:items-center justify-center p-6 md:p-10 lg:p-14 overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-lg space-y-6 py-6">
          {/* Mobile logo */}
          <Link to="/" className="md:hidden inline-block mb-4">
            <img src={LOGO_URL} alt="Amazonico 3000" className="h-14 object-contain" />
          </Link>

          {/* Header */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold">Registrazione</div>
            <h2 className="editorial-heading text-3xl md:text-4xl text-stone-900 mt-1">
              {isB2B ? "Registra l'azienda" : "Crea account"}
            </h2>
            <p className="text-sm text-stone-500 mt-2">
              {isB2B ? "Compila i dati aziendali. Il tuo account verrà verificato entro 24-48h." : "Un account per accedere a food box, ricette AI e club vini."}
            </p>
          </div>

          {/* Role switch */}
          <div className="grid grid-cols-2 gap-2 p-1 bg-stone-100 border border-stone-200">
            <button
              type="button"
              data-testid="role-b2b"
              onClick={() => setRole("buyer")}
              className={`flex items-center justify-center gap-2 py-3 text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${isB2B ? "bg-[#C9A961] text-white" : "text-stone-500 hover:text-stone-900"}`}
            >
              <Building2 className="h-3.5 w-3.5" /> Azienda B2B
            </button>
            <button
              type="button"
              data-testid="role-b2c"
              onClick={() => setRole("b2c_customer")}
              className={`flex items-center justify-center gap-2 py-3 text-[10px] uppercase tracking-[0.25em] font-bold transition-colors ${!isB2B ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-900"}`}
            >
              <User className="h-3.5 w-3.5" /> Cliente Privato
            </button>
          </div>

          {/* B2B section */}
          {isB2B && (
            <div className="space-y-5 pt-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold border-b border-stone-200 pb-2">Anagrafica azienda</div>

              <FField label="Ragione sociale" required>
                <Input data-testid="reg-company" required value={form.company} onChange={upd("company")} placeholder="es. Cantina Rossi SRL" className="rounded-none" />
              </FField>

              <div className="grid sm:grid-cols-2 gap-4">
                <FField label="Partita IVA / VAT" required>
                  <Input data-testid="reg-vat" required value={form.vat_number} onChange={upd("vat_number")} placeholder="IT01234567890" className="rounded-none" />
                </FField>
                <FField label="Paese" required>
                  <select value={form.country} onChange={upd("country")} className="w-full h-9 px-3 text-sm border border-stone-300 bg-white focus:border-[#C9A961] focus:outline-none">
                    {COUNTRIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                  </select>
                </FField>
              </div>

              <FField label="Tipo attività" required>
                <select value={form.business_type} onChange={upd("business_type")} className="w-full h-9 px-3 text-sm border border-stone-300 bg-white focus:border-[#C9A961] focus:outline-none">
                  {B2B_TYPES.map(b => <option key={b.v} value={b.v}>{b.l}</option>)}
                </select>
              </FField>

              <FField label="Focus categoria">
                <div className="grid grid-cols-3 gap-2">
                  {B2B_CATEGORIES.map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      onClick={() => setForm({ ...form, category_focus: c.v })}
                      className={`flex flex-col items-center gap-1.5 p-3 border transition-colors ${form.category_focus === c.v ? "border-[#C9A961] bg-[#C9A961]/10 text-stone-900" : "border-stone-200 text-stone-500 hover:border-stone-400"}`}
                    >
                      <c.icon className="h-4 w-4" />
                      <span className="text-[10px] uppercase tracking-[0.15em] font-bold">{c.l}</span>
                    </button>
                  ))}
                </div>
              </FField>

              <FField label="Sito web (opzionale)">
                <Input value={form.website} onChange={upd("website")} placeholder="https://www.tuaazienda.it" className="rounded-none" />
              </FField>

              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold border-b border-stone-200 pb-2 pt-2">Referente & credenziali</div>

              <FField label="Nome referente" required>
                <Input data-testid="reg-name" required value={form.name} onChange={upd("name")} placeholder="es. Marco Rossi" className="rounded-none" />
              </FField>

              <div className="grid sm:grid-cols-2 gap-4">
                <FField label="Email aziendale" required>
                  <Input data-testid="reg-email" type="email" required value={form.email} onChange={upd("email")} placeholder="info@azienda.it" className="rounded-none" />
                </FField>
                <FField label="Telefono">
                  <Input data-testid="reg-phone" value={form.contact_phone} onChange={upd("contact_phone")} placeholder="+39 ..." className="rounded-none" />
                </FField>
              </div>

              <FField label="Password" required help="Minimo 6 caratteri">
                <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="rounded-none" />
              </FField>

              <FField label="Note (opzionale)">
                <textarea rows={2} value={form.notes} onChange={upd("notes")} placeholder="Volumi indicativi, mercati target, esigenze specifiche..." className="w-full px-3 py-2 text-sm border border-stone-300 bg-white focus:border-[#C9A961] focus:outline-none rounded-none" />
              </FField>
            </div>
          )}

          {/* B2C section */}
          {!isB2B && (
            <div className="space-y-5">
              <FField label="Nome completo" required>
                <Input data-testid="reg-name" required value={form.name} onChange={upd("name")} placeholder="Nome e cognome" className="rounded-none" />
              </FField>
              <FField label="Email" required>
                <Input data-testid="reg-email" type="email" required value={form.email} onChange={upd("email")} className="rounded-none" />
              </FField>
              <FField label="Password" required help="Minimo 6 caratteri">
                <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={upd("password")} className="rounded-none" />
              </FField>
              <FField label="Paese">
                <select value={form.country} onChange={upd("country")} className="w-full h-9 px-3 text-sm border border-stone-300 bg-white focus:border-[#C9A961] focus:outline-none">
                  {COUNTRIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </FField>
            </div>
          )}

          {/* Privacy */}
          <label className="flex items-start gap-3 text-xs text-stone-600 leading-relaxed cursor-pointer">
            <input
              type="checkbox"
              checked={privacyOk}
              onChange={(e) => setPrivacyOk(e.target.checked)}
              data-testid="reg-privacy"
              className="mt-0.5 h-4 w-4 accent-[#C9A961]"
            />
            <span>
              Accetto la <Link to="/privacy" className="text-[#A88A4A] underline">Privacy Policy</Link> e i <Link to="/termini" className="text-[#A88A4A] underline">Termini di Servizio</Link>. Acconsento al trattamento dei dati aziendali per finalità commerciali B2B.
            </span>
          </label>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            data-testid="reg-submit"
            className="w-full h-12 rounded-none bg-[#C9A961] hover:bg-[#A88A4A] text-white text-[10px] uppercase tracking-[0.3em] font-bold"
          >
            {loading ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Invio in corso…</>
            ) : (
              <>{isB2B ? "Affiliati come Azienda B2B" : "Crea account"}</>
            )}
          </Button>

          <div className="text-center text-sm text-stone-500">
            Hai già un account? <Link to="/login" className="text-[#A88A4A] underline font-semibold">Accedi</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function FField({ label, required, help, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-700">
        {label}{required && <span className="text-[#A88A4A] ml-0.5">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {help && <div className="text-[10px] text-stone-400 mt-1">{help}</div>}
    </div>
  );
}
